import { useState } from "react";
import SectionHeader from "./SectionHeader";
import Reveal from "./Reveal";

const hotspots = [
  { id: "mattress", x: 38, y: 52, label: "Mattress & bed area", desc: "Seams, tufts and piping where bed bugs most commonly harbour." },
  { id: "frame", x: 38, y: 30, label: "Bed frame", desc: "Joints, screw holes and frame cavities are inspected and treated." },
  { id: "headboard", x: 62, y: 22, label: "Headboard", desc: "Cracks and fastener points behind and within the headboard." },
  { id: "furniture", x: 82, y: 60, label: "Furniture", desc: "Nightstands and nearby furniture seams and drawers." },
  { id: "baseboards", x: 12, y: 82, label: "Baseboards", desc: "Gaps along baseboards and the wall-floor junction." },
  { id: "crevices", x: 50, y: 78, label: "Cracks & crevices", desc: "Structural cracks where hiding is most prevalent." }
];

export default function TreatmentAreas() {
  const [active, setActive] = useState(null);
  const current = hotspots.find((h) => h.id === active);

  return (
    <section id="areas" className="border-b border-border bg-primary px-5 py-24 text-white lg:px-8 lg:py-32">
      <div className="mx-auto max-w-7xl">
        <SectionHeader
          dark
          kicker="Treatment Areas"
          title="MORE THAN A MATTRESS."
          subtitle="Treatment areas depend on the property and treatment requirements. This blueprint shows where bed bugs commonly hide — not a guarantee every spot is treated."
        />
        <div className="mt-14 grid gap-10 lg:grid-cols-[1.3fr_1fr]">
          <Reveal>
            <div className="relative aspect-[4/3] w-full overflow-hidden rounded-lg border border-white/10 bg-[#06091a]">
              <svg viewBox="0 0 100 75" className="h-full w-full">
                {/* room outline */}
                <rect x="4" y="6" width="92" height="66" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="0.3" />
                {/* headboard */}
                <rect x="30" y="16" width="40" height="8" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="0.4" />
                {/* bed */}
                <rect x="24" y="24" width="52" height="34" rx="1.5" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="0.4" />
                <line x1="24" y1="44" x2="76" y2="44" stroke="rgba(255,255,255,0.12)" strokeWidth="0.2" />
                {/* mattress seam */}
                <line x1="24" y1="40" x2="76" y2="40" stroke="rgba(255,255,255,0.18)" strokeWidth="0.25" strokeDasharray="1,1" />
                {/* nightstand */}
                <rect x="80" y="50" width="12" height="14" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="0.4" />
                {/* baseboard line */}
                <line x1="4" y1="71" x2="96" y2="71" stroke="rgba(255,255,255,0.2)" strokeWidth="0.3" />
                {/* crosshairs */}
                <line x1="0" y1="37.5" x2="100" y2="37.5" stroke="rgba(255,255,255,0.04)" strokeWidth="0.2" />
                <line x1="50" y1="0" x2="50" y2="75" stroke="rgba(255,255,255,0.04)" strokeWidth="0.2" />

                {hotspots.map((h) => {
                  const on = active === h.id;
                  return (
                    <g key={h.id} onMouseEnter={() => setActive(h.id)} onClick={() => setActive(h.id)} onFocus={() => setActive(h.id)} tabIndex={0} role="button" aria-label={h.label} aria-pressed={active === h.id} style={{ cursor: "pointer" }}>
                      <circle cx={h.x} cy={h.y} r={on ? 2.4 : 1.6} fill={on ? "#D72638" : "rgba(255,255,255,0.55)"}>
                        {on && <animate attributeName="r" values="2.4;3.2;2.4" dur="1.2s" repeatCount="indefinite" />}
                      </circle>
                      <circle cx={h.x} cy={h.y} r="5" fill="transparent" />
                    </g>
                  );
                })}
              </svg>
              <span className="absolute left-4 top-4 text-[9px] font-semibold uppercase tracking-[0.25em] text-white/60">Blueprint · Bedroom</span>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="flex h-full flex-col">
              <div className="min-h-[140px] rounded-lg border border-white/10 bg-white/[0.03] p-6">
                {current ? (
                  <>
                    <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-accent">{current.label}</span>
                    <p className="mt-3 text-sm leading-relaxed text-white/70">{current.desc}</p>
                  </>
                ) : (
                  <span className="text-sm text-white/70">Hover or tap a marker to view a treatment area.</span>
                )}
              </div>
              <ul className="mt-5 grid grid-cols-1 gap-2">
                {hotspots.map((h) => (
                  <li key={h.id}>
                    <button
                      onMouseEnter={() => setActive(h.id)}
                      onClick={() => setActive(h.id)}
                      aria-pressed={active === h.id}
                      className={`flex w-full items-center justify-between rounded-md border px-4 py-3 text-left text-sm transition-colors ${active === h.id ? "border-accent/40 bg-accent/10 text-white" : "border-white/10 text-white/70 hover:bg-white/5"}`}
                    >
                      <span>{h.label}</span>
                      <span className={`h-2 w-2 rounded-full ${active === h.id ? "bg-accent" : "bg-white/25"}`} />
                    </button>
                  </li>
                ))}
              </ul>
              <p className="mt-5 text-xs leading-relaxed text-white/70">Treatment areas depend on the property and treatment requirements.</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}